import * as React from 'react';
import { cn } from '@/lib/utils';

interface ToggleGroupContextShape {
  value: string | null;
  setValue: (next: string | null) => void;
}

const ToggleGroupCtx = React.createContext<ToggleGroupContextShape | null>(null);

interface ToggleGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Currently selected value (null = nothing selected). */
  value: string | null;
  /** Called with the next value. Click on the active item passes null (toggle off). */
  onChange: (next: string | null) => void;
}

/** Exclusive button group — children are <ToggleGroupItem value="...">.
 *  Buttons share borders cleanly: outer rounding lives on the group
 *  (overflow-hidden), inner dividers are hairline borders between items.
 *  No competing per-button border-radius. */
export function ToggleGroup({ value, onChange, className, children, ...rest }: ToggleGroupProps) {
  return (
    <ToggleGroupCtx.Provider value={{ value, setValue: onChange }}>
      <div
        role="group"
        className={cn(
          'inline-flex items-stretch overflow-hidden rounded-md border border-border bg-card',
          // Hairline divider between items via left border on every item except the first.
          '[&>button+button]:border-l [&>button+button]:border-l-border',
          className
        )}
        {...rest}
      >
        {children}
      </div>
    </ToggleGroupCtx.Provider>
  );
}

interface ToggleGroupItemProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string;
}

export const ToggleGroupItem = React.forwardRef<HTMLButtonElement, ToggleGroupItemProps>(
  ({ className, value, onClick, children, ...props }, ref) => {
    const ctx = React.useContext(ToggleGroupCtx);
    if (!ctx) throw new Error('ToggleGroupItem must be used inside ToggleGroup');
    const active = ctx.value === value;
    return (
      <button
        ref={ref}
        type="button"
        data-state={active ? 'on' : 'off'}
        aria-pressed={active}
        onClick={(e) => {
          ctx.setValue(active ? null : value);
          onClick?.(e);
        }}
        className={cn(
          // Layout / typography — note: NO border-radius here, group owns rounding.
          'relative inline-flex h-9 items-center justify-center whitespace-nowrap px-3 text-xs font-medium tracking-[0.2px]',
          'transition-colors outline-none select-none',
          'focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring/50',
          // Idle state.
          'bg-card text-muted-foreground hover:bg-muted hover:text-foreground',
          // Active state — bring forward so neighbour borders don't cover anything.
          active && 'z-10 bg-primary-muted text-primary-muted-foreground',
          'disabled:pointer-events-none disabled:opacity-50',
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
ToggleGroupItem.displayName = 'ToggleGroupItem';
